import { useQuery } from '@tanstack/react-query'
import ms from 'ms'

import { groupShelvesByCategory, type ShelfGroup } from '@components/storeMap/categoryGrouping/shelfGrouping'
import { useStoreMapContext } from '@components/storeMap/provider/StoreMapContext'
import { type UseLayoutOptions, useLayoutTransformed } from '@state/layout/queries'
import useViewContextAwareHttpClient from '@utils/ViewContextAwareHttpClient'

// Define the gradient colors
const colors = [
  { percent: 0, r: 0x17, g: 0xB2, b: 0x6A }, // 0%: --color-success-500 (green)
  { percent: 50, r: 0xF7, g: 0x90, b: 0x09 }, // 50%: --color-warning-500 (orange)
  { percent: 100, r: 0xF0, g: 0x44, b: 0x38 }, // 100%: --color-error-500 (red)
]

export function getShelfColorByUnknownPercentage(percentage: number | undefined) {
  if (percentage === undefined) return undefined

  // Clamp the percentage between 0 and 100 to be safe
  const p = Math.max(0, Math.min(100, percentage))

  let color1: typeof colors[0], color2: typeof colors[0], localPercent: number

  if (p <= 50) {
    // Interpolate between green (0%) and orange (50%)
    color1 = colors[0]!
    color2 = colors[1]!
    localPercent = p / 50 // Normalize to 0-1 range
  } else {
    // Interpolate between orange (50%) and red (100%)
    color1 = colors[1]!
    color2 = colors[2]!
    localPercent = (p - 50) / 50 // Normalize to 0-1 range
  }

  // Linear interpolation between the two colors
  const r = Math.round(color1.r + (color2.r - color1.r) * localPercent)
  const g = Math.round(color1.g + (color2.g - color1.g) * localPercent)
  const b = Math.round(color1.b + (color2.b - color1.b) * localPercent)

  // Return RGB format directly
  return `rgb(${r}, ${g}, ${b})`
}

/**
 * Hook to fetch the layout with specified options where mapOrigin is derived from the StoreMap context.
 */
export function useLayoutWithMapOrigin({ complete = false }: Omit<UseLayoutOptions, 'mapOrigin'> = {}) {
  const { mapOrigin } = useStoreMapContext()
  return useLayoutTransformed({ mapOrigin, complete })
}

export function useShelfCategoryGroups() {
  const { mapOrigin } = useStoreMapContext()
  const { data: layout } = useLayoutWithMapOrigin()
  const { storeId, viewKey } = useViewContextAwareHttpClient()

  return useQuery<ShelfGroup[]>({
    queryKey: ['shelf-categories', storeId, viewKey, { mapOrigin }],
    queryFn: () => groupShelvesByCategory(layout?.shelves ?? []),
    staleTime: ms('1d'),
    enabled: layout !== undefined,
  })
}